import React, { useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { STORAGE_KEYS, readStorageValue, writeStorageValue } from '../../utils/storageKeys';

// Supported languages
export const SUPPORTED_LANGUAGES = ['en', 'it', 'es'];
export const DEFAULT_LANGUAGE = 'en';

/**
 * LanguageWrapper component that handles URL-based language routing
 * Similar to how MDN uses /en-US/ and /fr/ in their URLs
 */
export function LanguageWrapper() {
  const { lang } = useParams();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // If language from URL is valid and different from current, change it
    if (lang && SUPPORTED_LANGUAGES.includes(lang)) {
      if (i18n.language !== lang) {
        i18n.changeLanguage(lang);
        writeStorageValue(window.localStorage, STORAGE_KEYS.language, lang);
      }
    }
  }, [lang, i18n]);

  // If no valid language in URL, redirect to appropriate language
  useEffect(() => {
    if (!lang || !SUPPORTED_LANGUAGES.includes(lang)) {
      // Get browser language preference or saved preference
      const browserLang = navigator.language.split('-')[0];
      const savedLang = readStorageValue(window.localStorage, STORAGE_KEYS.language)?.value;
      const preferredLang = savedLang || (SUPPORTED_LANGUAGES.includes(browserLang) ? browserLang : DEFAULT_LANGUAGE);
      
      // Redirect to the same path with language prefix
      const currentPath = location.pathname === '/' ? '/dashboard' : location.pathname;
      navigate(`/${preferredLang}${currentPath}${location.search}`, { replace: true });
    }
  }, [lang, location, navigate]);

  // Don't render children until we have a valid language
  if (!lang || !SUPPORTED_LANGUAGES.includes(lang)) {
    return null;
  }

  return <Outlet />;
}

/**
 * Component to handle the root redirect
 * Redirects from / to /:lang/dashboard based on user preference
 */
export function LanguageRedirect() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Get browser language preference or saved preference
    const browserLang = navigator.language.split('-')[0];
    const savedLang = readStorageValue(window.localStorage, STORAGE_KEYS.language)?.value;
    const preferredLang = savedLang || (SUPPORTED_LANGUAGES.includes(browserLang) ? browserLang : DEFAULT_LANGUAGE);
    
    // Redirect to dashboard with language prefix
    navigate(`/${preferredLang}/dashboard${location.search}`, { replace: true });
  }, [navigate, location]);

  return null;
}

/**
 * Hook to get current language from URL
 */
export function useLanguage() {
  const { lang } = useParams();
  const { i18n } = useTranslation();
  
  return {
    currentLang: lang || i18n.language || DEFAULT_LANGUAGE,
    supportedLanguages: SUPPORTED_LANGUAGES,
    defaultLanguage: DEFAULT_LANGUAGE
  };
}

/**
 * Hook to generate language-aware navigation
 */
export function useLanguageNavigation() {
  const { lang } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();
  
  const currentLang = lang || i18n.language || DEFAULT_LANGUAGE;

  /**
   * Navigate to a path while preserving language
   */
  const navigateTo = useCallback((path, options) => {
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    navigate(`/${currentLang}${cleanPath}`, options);
  }, [currentLang, navigate]);

  /**
   * Switch to a different language while staying on the same page
   */
  const switchLanguage = useCallback((newLang) => {
    if (SUPPORTED_LANGUAGES.includes(newLang)) {
      // Get the current path without the language prefix
      const pathWithoutLang = location.pathname.replace(/^\/(en|it|es)/, '');
      const newPath = `/${newLang}${pathWithoutLang || '/dashboard'}${location.search}`;
      
      // Update i18n and localStorage
      i18n.changeLanguage(newLang);
      writeStorageValue(window.localStorage, STORAGE_KEYS.language, newLang);
      
      // Navigate to new language URL
      navigate(newPath, { replace: true });
    }
  }, [i18n, location.pathname, location.search, navigate]);

  /**
   * Get a language-prefixed path
   */
  const getLocalizedPath = useCallback((path) => {
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return `/${currentLang}${cleanPath}`;
  }, [currentLang]);

  return {
    navigateTo,
    switchLanguage,
    getLocalizedPath,
    currentLang
  };
}
